// Live Teams presence for the agents currently on screen in the phone-menu
// guide. The guide reports the visible ids; this hook polls POST /presence
// for them (every minute while the page is visible, every five when the
// tenant has not enabled it) and accumulates the answers in one map.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { PresenceInfo } from '../views/phoneMenu/lib/callFlowStatus';

export type PresenceState = 'ok' | 'consent' | 'unavailable' | 'loading';

const POLL_OK_MS = 60_000;
const POLL_DEGRADED_MS = 5 * 60_000;
const MAX_IDS = 650;

export function usePresence(enabled: boolean) {
  const [presence, setPresence] = useState<Map<string, PresenceInfo>>(() => new Map());
  const [presenceState, setPresenceState] = useState<PresenceState>('loading');
  const [consentUrl, setConsentUrl] = useState<string | undefined>();
  const idsRef = useRef<string[]>([]);
  const keyRef = useRef('');
  const stateRef = useRef<PresenceState>('loading');
  const [idsKey, setIdsKey] = useState('');

  const setVisibleIds = useCallback((ids: string[]) => {
    const unique = [...new Set(ids.map(i => i.toLowerCase()))].sort();
    const key = unique.join(',');
    if (key === keyRef.current) return;
    keyRef.current = key;
    idsRef.current = unique;
    setIdsKey(key);
  }, []);

  useEffect(() => {
    if (!enabled || !idsKey) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timer = setTimeout(tick, stateRef.current === 'ok' || stateRef.current === 'loading' ? POLL_OK_MS : POLL_DEGRADED_MS);
    };
    const update = (next: PresenceState) => { stateRef.current = next; setPresenceState(next); };
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) { schedule(); return; }
      try {
        const res = await api.getPresence(idsRef.current.slice(0, MAX_IDS));
        if (cancelled) return;
        if (res.available) {
          setPresence(prev => {
            const map = new Map(prev);
            for (const p of res.presences) map.set(p.id.toLowerCase(), { availability: p.availability, activity: p.activity });
            return map;
          });
          update('ok');
        } else {
          setConsentUrl(res.consentUrl);
          update(res.reason === 'consent' ? 'consent' : 'unavailable');
        }
      } catch {
        if (!cancelled) update('unavailable');
      }
      if (!cancelled) schedule();
    };
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) { if (timer) clearTimeout(timer); void tick(); }
    };
    void tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, idsKey]);

  return { presence, presenceState, consentUrl, setVisibleIds };
}
